import { CalculatedColumn, RenderSortStatusProps } from "react-data-grid";
import {
  AlphaNumbericType,
  IDataSetColumnsModel,
  ITableConfigModel,
  ObjectModel,
} from "../../tsmodels/SharedModel";
import {
  faSortUp,
  faSortDown,
  faCaretUp,
  faCaretDown,
  faFileExport,
  faFileCode,
  faFileCsv,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import {
  valueFormat,
  dateFormatTo,
  convertTimeToEST,
  performActions,
  getStringValue,
} from "../../helpers/GenericHelpers";
import moment from "moment";
import "moment-timezone";
import TextInput from "../inputs/TextInput";
import { NavigateFunction } from "react-router-dom";
import { COMA_SYMBOL } from "../../helpers/Constants";
import {
  ColumnDataTypeEnum,
  SubDataTypeEnum,
  FilterTypeEnum,
} from "../../tsmodels/FilterModel";

interface IRenderSortStatusProps extends RenderSortStatusProps {
  config: ITableConfigModel;
}

export const renderSortStatus = (props: IRenderSortStatusProps) => {
  const { sortDirection, priority, config } = props;

  return config?.sortable ? (
    <>
      {sortDirection !== undefined
        ? sortDirection === "ASC"
          ? "\u2B9D"
          : "\u2B9F"
        : null}
      <span className="sortPriorityClassname">{priority}</span>
    </>
  ) : (
    <></>
  );
};

export const formatCardType = (value: string) =>
  value ? (
    <>
      <span className="format-text">XXXX-</span>
      <span className="format-value">{value}</span>
    </>
  ) : (
    <></>
  );

// renderCell
export const renderCell = (
  column: CalculatedColumn<any, any>,
  row: any,
  tabIndex: number,
  onRowChange: (row: any) => void,
  each: any,
  config: ITableConfigModel,
  exportDropdown?: string,
  toggleExportDropdown?: (value: string) => void
) => {
  const key = getStringValue(each?.columnName);
  const value = row?.[key];
  const columnType = each?.columnDatatype;
  switch (columnType) {
    case ColumnDataTypeEnum.NUMBER:
      if (each?.subType === SubDataTypeEnum.CURRENCY)
        return value ? (
          <div>{valueFormat(value, COMA_SYMBOL)}</div>
        ) : (
          <div>{value || ""}</div>
        );
      else {
        return value ?? "";
      }

    case ColumnDataTypeEnum.STRING:
      if (each?.subType === SubDataTypeEnum.CREDIT_CARD_TYPE)
        return formatCardType(value);
      if (each?.subType === SubDataTypeEnum.TIME_EST) {
        return value ? convertTimeToEST(value) : "";
      }
      return decodeURI(value) ?? "";

    case ColumnDataTypeEnum.DATE:
      if (each?.subType === SubDataTypeEnum.TIME_EST) {
        return value ? convertTimeToEST(value) : "";
      }
      return value ? dateFormatTo(value) : "";

    case ColumnDataTypeEnum.RENDER_HTML:
      return each?.formatter(row, column, onRowChange) ?? <></>;

    case ColumnDataTypeEnum.BUTTON:
      return each?.formatter(key.toUpperCase(), row) ?? <Button>{key}</Button>;

    case ColumnDataTypeEnum.EXPANDED:
      return (
        <CellExpanderFormatter
          expanded={row.expanded}
          tabIndex={tabIndex}
          onCellExpand={() => {
            onRowChange({ ...row, expanded: !row.expanded });
          }}
        />
      );

    case ColumnDataTypeEnum.EXPORT:
      return (
        <Dropdown
          isOpen={row?.rowId === exportDropdown}
          toggle={() =>
            row?.rowId &&
            toggleExportDropdown &&
            toggleExportDropdown(row?.rowId)
          }
          direction="down"
          className="export-btn"
        >
          <DropdownToggle>
            <FontAwesomeIcon icon={faFileExport} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem>
              <FontAwesomeIcon icon={faFileCsv} /> <span>CSV</span>
            </DropdownItem>
            <DropdownItem>
              <FontAwesomeIcon icon={faFilePdf} /> <span>PDF</span>
            </DropdownItem>
            <DropdownItem>
              <FontAwesomeIcon icon={faFileCode} /> <span>XML</span>
            </DropdownItem>
            <DropdownItem>
              <FontAwesomeIcon icon={faFileExcel} /> <span>XSLX</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      );

    default:
      return value ?? "";
  }
};

// renderHeaderFilters
export const renderHeaderFilters = (p: any, config: ITableConfigModel) => (
  <div className="filterHeaderColumn">
    <div className="name">{p?.column?.name}</div>
    {config?.sortable && !p?.column?.info?.disableFunctions ? (
      <div className="sort-options">
        <FontAwesomeIcon
          icon={faSortUp}
          className={`sortIconClassName up ${
            p?.sortDirection === "ASC" ? "active" : "in-active"
          }`}
        />
        <FontAwesomeIcon
          icon={faSortDown}
          className={`sortIconClassName down ${
            p?.sortDirection === "DESC" ? "active" : "in-active"
          }`}
        />
      </div>
    ) : (
      <></>
    )}
  </div>
);

// renderEditCell
export const renderEditCell = (
  row: any,
  column: any,
  onRowChange: any,
  each: any
) => {
  const key = getStringValue(each?.columnName);
  const value = row?.[key];
  const columnType = each?.columnDatatype;
  switch (columnType) {
    case ColumnDataTypeEnum.RENDER_HTML:
      return each?.formatter(row, column, onRowChange) ?? <></>;

    default:
      return value ?? "";
  }
};

export const renderSummaryCell = (
  summaryProps: any,
  config: ITableConfigModel
) => {
  const columnInfo = summaryProps?.column?.info;
  const value = summaryProps?.row?.[summaryProps?.column?.key];

  if (config?.enableSummaryRows && columnInfo) {
    // Todo: change this to render cell common funtionality
    if (columnInfo?.columnDatatype === ColumnDataTypeEnum.NUMBER) {
      if (columnInfo?.subType === SubDataTypeEnum.CURRENCY)
        return value ? (
          <div>{valueFormat(value, COMA_SYMBOL)}</div>
        ) : (
          <div>{value || ""}</div>
        );
      else {
        return value ?? "";
      }
    }
    return value ?? "";
  }
  return "";
};

// displayDataList
export const displayDataList = (
  gridColumns: IDataSetColumnsModel[],
  rows: ObjectModel<AlphaNumbericType>[]
) => {
  const filteredColumns = gridColumns?.filter(
    (each) => each?.columnDatatype === ColumnDataTypeEnum.STRING
  );
  return filteredColumns?.map((each) => {
    const options = Array.from(
      new Set(
        rows?.length > 0
          ? rows?.map(
              (r: ObjectModel<AlphaNumbericType>) =>
                r?.[getStringValue(each?.columnName)]
            )
          : []
      )
    )?.map((d) => ({
      label: d,
      value: d,
    }));

    return (
      <datalist id={getStringValue(each?.columnName)}>
        {options.map(({ label, value }: any) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </datalist>
    );
  });
};

// getType
export const getType = (type: string, gridColumns: IDataSetColumnsModel[]) =>
  gridColumns?.find((each) => getStringValue(each?.columnName) === type)
    ?.columnDatatype;

export const getCustomSortFunction = (
  type: string,
  gridColumns: IDataSetColumnsModel[]
) =>
  gridColumns?.find((each) => getStringValue(each?.columnName) === type)
    ?.customSortFunction;

// getComparator
export function getComparator(
  sortColumn: string,
  gridColumns: IDataSetColumnsModel[]
): any {
  const type = getType(sortColumn, gridColumns);
  const customSortFunction = getCustomSortFunction(sortColumn, gridColumns);
  if (customSortFunction) {
    return customSortFunction
  }
  switch (type) {
    case ColumnDataTypeEnum.STRING:
      return (a: any, b: any) => {
        return a?.[sortColumn] && b?.[sortColumn]
          ? a?.[sortColumn].localeCompare(b?.[sortColumn])
          : 1;
      };
    case ColumnDataTypeEnum.NUMBER:
      return (a: any, b: any) => {
        return compareNumbers(a?.[sortColumn],b?.[sortColumn])
         
      };
    case ColumnDataTypeEnum.DATE:
      return (a: any, b: any) => {
        if (a?.[sortColumn] && b?.[sortColumn]) {
          const date1 = moment(a?.[sortColumn]);
          const date2 = moment(b?.[sortColumn]);
          return date1.isBefore(date2) ? -1 : 1;
        } else return 1;
      };
    default:
      return (a: any, b: any) => {
        return a?.[sortColumn] && b?.[sortColumn]
          ? a?.[sortColumn]?.localeCompare(b?.[sortColumn])
          : 1;
      };
  }
}
function compareNumbers(a:number, b:number) {
  if (a < b) {
    return -1;
    
    // a comes before b
  } else if (a > b) {
    return 1;
    
    // a comes after b
  } else {
    return 0;
  
    // a and b are equal
  }
}

export const CellExpanderFormatter = ({
  tabIndex,
  expanded,
  onCellExpand,
}: any) => {
  function handleKeyDown(e: React.KeyboardEvent<HTMLSpanElement>) {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onCellExpand();
    }
  }

  return (
    <div
      className="cellExpandClassname"
      onClick={onCellExpand}
      onKeyDown={handleKeyDown}
    >
      <span tabIndex={tabIndex}>
        {expanded ? (
          <FontAwesomeIcon icon={faCaretUp} />
        ) : (
          <FontAwesomeIcon icon={faCaretDown} />
        )}
      </span>
    </div>
  );
};

export const exportColumn = {
  id: "e97d202b-6f36-4a89-913f-b727c3db54a8",
  columnName: "export",
  columnDatatype: ColumnDataTypeEnum.EXPORT,
  aliasName: "export",
  hideHeader: false,
  disableFunctions: true,
};

export const expandedColumn = {
  id: "e97d202b-6f36-4a89-913f-b727c3db54a9",
  columnName: "expanded",
  columnDatatype: ColumnDataTypeEnum.EXPANDED,
  aliasName: "expanded",
  hideHeader: true,
  disableFunctions: true,
};

export const optionsColumn = {
  id: "e97d202b-6f36-4a89-913f-b727c3db54a0",
  columnName: "options",
  columnDatatype: ColumnDataTypeEnum.OPTIONS,
  aliasName: "options",
  hideHeader: true,
  disableFunctions: true,
};

export const expandedColumnFields = {
  minWidth: 30,
  width: 40,
};

// function to check if the values are valid are not - pass value and type
export const hasValues = (value: any, type: ColumnDataTypeEnum) => {
  if (type === ColumnDataTypeEnum.STRING) {
    return !!value;
  }
  // assuming remaing are of type array - change later
  else {
    const removeNulls = value ? value?.filter(Boolean) : [];
    return removeNulls?.length > 0;
  }
};

// converting table filters to api payload model
export const modifyGridFilters = (filters: any) => {
  const keys = filters ? Object.keys(filters) : [];

  // get keys for only the objects that have valid values
  const filteredKeys = keys?.filter((key) => {
    const keyEach = filters?.[key];
    // function to check if the values are valid are not - pass value and type
    return hasValues(keyEach?.value, keyEach?.type);
  });

  // final object contruction
  const filterDto = filteredKeys?.map((filKey) => ({
    columnName: filKey,
    columnDataType: filters?.[filKey]?.type,
    values: filters?.[filKey]?.value,
  }));

  return filterDto;
};

export const filterTypeMap = () => {
  const keys = Object.keys(FilterTypeEnum);
  return (
    keys?.map((key) => ({
      label: key,
      value: (FilterTypeEnum as any)?.[key],
    })) || []
  );
};
